import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import {
  ExclamationIcon,
  PhoneIcon,
  MailIcon,
  DocumentDownloadIcon,
} from "@heroicons/react/outline";

const OverdueReport = () => {
  const navigate = useNavigate();
  const [overdues, setOverdues] = useState([]);
  const [filter, setFilter] = useState({
    daysOverdue: "all",
    sortBy: "amount",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOverdues();
  }, [filter]);

  const fetchOverdues = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData = [
        {
          _id: "1",
          planNumber: "JP2024001",
          customerName: "John Doe",
          phoneNumber: "9876543210",
          email: "john@example.com",
          installmentNumber: 3,
          dueDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
          amountDue: 5000,
          daysOverdue: 15,
          totalOverdueAmount: 10000,
          lastContactDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        },
        {
          _id: "2",
          planNumber: "JP2024002",
          customerName: "Jane Smith",
          phoneNumber: "9876543211",
          email: "jane@example.com",
          installmentNumber: 2,
          dueDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          amountDue: 7500,
          daysOverdue: 30,
          totalOverdueAmount: 15000,
          lastContactDate: null,
        },
      ];
      setOverdues(mockData);
    } catch (error) {
      console.error("Failed to fetch overdues:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (days) => {
    if (days > 30) return "text-danger bg-danger-light";
    if (days > 15) return "text-warning bg-warning-light";
    return "text-secondary bg-secondary-light";
  };

  const handleContact = (customer, method) => {
    console.log(`Contacting ${customer.customerName} via ${method}`);
  };

  const handleExport = () => {
    console.log("Exporting overdue report");
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Overdue Report</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              {overdues.length} plans with overdue installments
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/jewellery/reports")}
              className="text-neutral-dark hover:text-text"
            >
              ← Back
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
            >
              <DocumentDownloadIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-background rounded-lg shadow p-6">
          <p className="text-sm text-neutral-dark">Total Overdue</p>
          <p className="text-2xl font-bold text-danger">
            {formatAmount(
              overdues.reduce((sum, o) => sum + o.totalOverdueAmount, 0)
            )}
          </p>
        </div>
        <div className="bg-background rounded-lg shadow p-6">
          <p className="text-sm text-neutral-dark">Critical (&gt;30 days)</p>
          <p className="text-2xl font-bold text-danger">
            {overdues.filter((o) => o.daysOverdue > 30).length}
          </p>
        </div>
        <div className="bg-background rounded-lg shadow p-6">
          <p className="text-sm text-neutral-dark">Warning (15-30 days)</p>
          <p className="text-2xl font-bold text-warning">
            {
              overdues.filter((o) => o.daysOverdue > 15 && o.daysOverdue <= 30)
                .length
            }
          </p>
        </div>
        <div className="bg-background rounded-lg shadow p-6">
          <p className="text-sm text-neutral-dark">Recent (&lt;15 days)</p>
          <p className="text-2xl font-bold text-secondary">
            {overdues.filter((o) => o.daysOverdue <= 15).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex gap-4">
          <select
            value={filter.daysOverdue}
            onChange={(e) =>
              setFilter({ ...filter, daysOverdue: e.target.value })
            }
            className="px-4 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="all">All Overdues</option>
            <option value="7">7+ Days</option>
            <option value="15">15+ Days</option>
            <option value="30">30+ Days</option>
          </select>
          <select
            value={filter.sortBy}
            onChange={(e) => setFilter({ ...filter, sortBy: e.target.value })}
            className="px-4 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="amount">Sort by Amount</option>
            <option value="days">Sort by Days Overdue</option>
            <option value="customer">Sort by Customer</option>
          </select>
        </div>
      </div>

      {/* Overdue List */}
      <div className="bg-background shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-neutral-light">
          <thead className="bg-neutral-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Plan / Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Overdue Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Last Contact
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-neutral-light">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : overdues.length > 0 ? (
              overdues.map((overdue) => (
                <tr key={overdue._id} className="hover:bg-neutral-light">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-text">
                        {overdue.planNumber}
                      </p>
                      <p className="text-sm text-neutral-dark">
                        {overdue.customerName}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm text-text">
                        Installment #{overdue.installmentNumber}
                      </p>
                      <p className="text-sm text-neutral-dark">
                        Due: {format(new Date(overdue.dueDate), "dd MMM yyyy")}
                      </p>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${getSeverityColor(
                          overdue.daysOverdue
                        )}`}
                      >
                        {overdue.daysOverdue} days overdue
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-text">
                        {formatAmount(overdue.amountDue)}
                      </p>
                      <p className="text-xs text-neutral-dark">
                        Total: {formatAmount(overdue.totalOverdueAmount)}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {overdue.lastContactDate ? (
                      <div>
                        <p className="text-sm text-text">
                          {format(new Date(overdue.lastContactDate), "dd MMM")}
                        </p>
                        <p className="text-xs text-neutral-dark">
                          {differenceInDays(
                            new Date(),
                            new Date(overdue.lastContactDate)
                          )}{" "}
                          days ago
                        </p>
                      </div>
                    ) : (
                      <span className="text-sm text-danger">
                        Never contacted
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleContact(overdue, "phone")}
                        className="p-2 bg-success text-text-on-primary rounded-lg hover:bg-success-dark"
                        title="Call Customer"
                      >
                        <PhoneIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleContact(overdue, "email")}
                        className="p-2 bg-secondary text-text-on-primary rounded-lg hover:bg-secondary-dark"
                        title="Email Customer"
                      >
                        <MailIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-4 text-center text-neutral-dark"
                >
                  No overdue installments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OverdueReport;
